import { type ReactElement, useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../../lib/api";
import { cn } from "../../lib/utils";
import type { MotifSignalement } from "../contract";
import { issueIncertaine } from "../diffusion-api";
import { useSignaler } from "./api";
import {
  type CodeErreurSignalement,
  ERREURS_SIGNALEMENT,
  LIBELLES_MOTIFS,
  MOTIFS,
  SIGNALEMENT,
} from "./publique-texts";

/**
 * La fenetre de signalement d'une carte (maquette `carte.html`, etat `signaler`) : trois motifs, un bouton, et la
 * promesse que Nicolas ne sait pas qui signale. Une fenetre de dialogue a la main : le focus entre dans la fenetre,
 * Tab y reste, Echap la ferme (un ecouteur du document), et le focus retourne au bouton qui l'a ouverte (c'est l'appelant qui le lui rend).
 *
 * Rien du serveur n'est affiche : un refus devient l'une des phrases d'`ERREURS_SIGNALEMENT`.
 */

const BOUTON =
  "min-h-11 rounded-md border-2 px-4 text-[20px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

/** La phrase d'un echec : le code de l'API s'il est de la liste, une issue incertaine, sinon la phrase generale. */
export function phraseDErreur(erreur: unknown): string {
  if (issueIncertaine(erreur)) return ERREURS_SIGNALEMENT.incertain;
  if (erreur instanceof ApiError && Object.hasOwn(ERREURS_SIGNALEMENT, erreur.code)) {
    const code = erreur.code as CodeErreurSignalement;
    if (code !== "autre" && code !== "incertain") return ERREURS_SIGNALEMENT[code];
  }
  return ERREURS_SIGNALEMENT.autre;
}

const FOCALISABLES =
  'input:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

export function SignalementDialog({
  reference,
  onFermer,
}: {
  reference: string;
  onFermer: () => void;
}): ReactElement {
  const titreId = useId();
  const introId = useId();
  const [motif, setMotif] = useState<MotifSignalement>("blessante");
  const signaler = useSignaler(reference);
  const fenetre = useRef<HTMLDivElement>(null);
  const envoye = signaler.isSuccess;

  // Le focus entre dans la fenetre a l'ouverture.
  useEffect(() => {
    const premier = fenetre.current?.querySelector<HTMLElement>(FOCALISABLES);
    premier?.focus();
  }, []);

  // Une fois le merci affiche, le focus passe a « Fermer » : le formulaire a disparu.
  useEffect(() => {
    if (envoye) {
      fenetre.current?.querySelector<HTMLElement>("button")?.focus();
    }
  }, [envoye]);

  // Echap ferme, Tab reste dans la fenetre : un ecouteur du document, pose tant que la fenetre est ouverte.
  useEffect(() => {
    function surClavier(evenement: KeyboardEvent): void {
      if (evenement.key === "Escape") {
        evenement.stopPropagation();
        onFermer();
        return;
      }
      if (evenement.key !== "Tab") return;
      const elements = [...(fenetre.current?.querySelectorAll<HTMLElement>(FOCALISABLES) ?? [])];
      const premier = elements[0];
      const dernier = elements[elements.length - 1];
      if (premier === undefined || dernier === undefined) return;
      const actif = document.activeElement;
      if (evenement.shiftKey && actif === premier) {
        evenement.preventDefault();
        dernier.focus();
      } else if (!evenement.shiftKey && actif === dernier) {
        evenement.preventDefault();
        premier.focus();
      }
    }
    document.addEventListener("keydown", surClavier);
    return () => document.removeEventListener("keydown", surClavier);
  }, [onFermer]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/65 p-4 md:items-center">
      <div
        ref={fenetre}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titreId}
        aria-describedby={introId}
        className="police-texte flex w-full max-w-[520px] flex-col gap-4 rounded-md border-2 border-primary bg-card p-5 text-[22px] text-card-foreground"
      >
        <h2 id={titreId} className="police-titre m-0 text-[28px] text-primary">
          {SIGNALEMENT.titre}
        </h2>
        {envoye ? (
          <>
            <p id={introId} role="status" className="m-0">
              {SIGNALEMENT.merci}
            </p>
            <div className="flex">
              <button
                type="button"
                onClick={onFermer}
                className={cn(BOUTON, "border-primary bg-primary text-primary-foreground")}
              >
                {SIGNALEMENT.fermer}
              </button>
            </div>
          </>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              signaler.mutate(motif);
            }}
          >
            <p id={introId} className="m-0">
              {SIGNALEMENT.intro}
            </p>
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="mb-1 p-0 text-[20px] text-muted-foreground">
                {SIGNALEMENT.pourquoi}
              </legend>
              {MOTIFS.map((code) => (
                <label key={code} className="flex min-h-11 cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="motif"
                    value={code}
                    checked={motif === code}
                    onChange={() => setMotif(code)}
                    className="size-5 accent-[var(--primary)]"
                  />
                  <span>{LIBELLES_MOTIFS[code]}</span>
                </label>
              ))}
            </fieldset>
            {signaler.isError ? (
              <p role="alert" className="m-0 text-destructive">
                {phraseDErreur(signaler.error)}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={signaler.isPending}
                className={cn(BOUTON, "border-primary bg-primary text-primary-foreground")}
              >
                {signaler.isPending ? SIGNALEMENT.envoi : SIGNALEMENT.envoyer}
              </button>
              <button
                type="button"
                onClick={onFermer}
                className={cn(BOUTON, "border-input bg-background hover:bg-muted")}
              >
                {SIGNALEMENT.annuler}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
